import React, { useEffect, useState } from 'react';
import { Box, Typography, keyframes } from '@mui/material';
import { SparklesCore } from './SparklesCore';
import { BRAND_LOGO_LOGIN } from '../config/brandAssets';
import { useAuthScreenViewport } from '../utils/useAuthScreenViewport';

/** Tokens — ver .cursor/skills/frontend-design/klinvia-tokens.md */
const TOKENS = {
  navyDeep: '#0B1220',
  navyMid: '#1E293B',
  clinicalPaper: '#F1F5F9',
  primary: '#2563EB',
  primarySoft: '#3B82F6',
  teal: '#0D9488',
  tealBright: '#2DD4BF',
  slateMuted: '#64748B',
  slateFaint: '#94A3B8',
} as const;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ekgDraw = keyframes`
  from { stroke-dashoffset: 320; }
  to { stroke-dashoffset: 0; }
`;

const scanLine = keyframes`
  0% { transform: translateX(-100%); opacity: 0; }
  20% { opacity: 0.6; }
  100% { transform: translateX(100%); opacity: 0; }
`;

const STEPS = [
  { id: 'auth', label: 'Autenticação' },
  { id: 'session', label: 'Sessão' },
  { id: 'perms', label: 'Permissões' },
] as const;

const STATUS_BY_STEP = [
  'Verificando credenciais',
  'Validando sessão ativa',
  'Carregando permissões',
];

interface AuthLoadingScreenProps {
  variant?: 'dark' | 'light';
}

const EkgTrace: React.FC<{ color: string; reducedMotion: boolean }> = ({ color, reducedMotion }) => (
  <Box sx={{ width: '100%', height: 36, overflow: 'hidden', position: 'relative' }}>
    {!reducedMotion && (
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(90deg, transparent, ${color}22, transparent)`,
          animation: `${scanLine} 2.8s ease-in-out infinite`,
          pointerEvents: 'none',
        }}
      />
    )}
    <Box
      component="svg"
      viewBox="0 0 320 36"
      preserveAspectRatio="none"
      sx={{ width: '100%', height: '100%', display: 'block' }}
      aria-hidden
    >
      <path
        d="M0 18 H40 L48 18 L54 8 L60 28 L66 18 H100 L108 18 L114 4 L120 32 L126 18 H160 L168 18 L174 12 L180 24 L186 18 H220 L228 18 L234 10 L240 26 L246 18 H320"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="320"
        strokeDashoffset={reducedMotion ? 0 : 320}
        style={reducedMotion ? undefined : { animation: `${ekgDraw} 2.4s ease-in-out infinite alternate` }}
        opacity={0.85}
      />
    </Box>
  </Box>
);

const AuthLoadingScreen: React.FC<AuthLoadingScreenProps> = ({ variant = 'dark' }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);
  const isDark = variant === 'dark';
  useAuthScreenViewport({ darkBackground: isDark });

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const handler = () => setReducedMotion(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (reducedMotion) return undefined;
    const id = window.setInterval(() => {
      setStepIndex((i) => (i + 1) % STEPS.length);
    }, 2200);
    return () => window.clearInterval(id);
  }, [reducedMotion]);

  const ekgColor = isDark ? TOKENS.tealBright : TOKENS.teal;
  const gridColor = isDark ? 'rgba(148,163,184,0.06)' : 'rgba(100,116,139,0.08)';

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label={STATUS_BY_STEP[stepIndex]}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        background: isDark
          ? `linear-gradient(160deg, ${TOKENS.navyDeep} 0%, ${TOKENS.navyMid} 55%, ${TOKENS.navyDeep} 100%)`
          : TOKENS.clinicalPaper,
        backgroundImage: `
          linear-gradient(${gridColor} 1px, transparent 1px),
          linear-gradient(90deg, ${gridColor} 1px, transparent 1px)
        `,
        backgroundSize: '24px 24px',
      }}
    >
      {isDark && !reducedMotion && (
        <>
          <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.7 }}>
            <SparklesCore
              background="transparent"
              minSize={0.4}
              maxSize={1}
              speed={1.2}
              particleColor={TOKENS.primarySoft}
              particleDensity={28}
              className="h-full w-full"
              fitParent
            />
          </Box>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: `radial-gradient(ellipse 55% 45% at 50% 35%, rgba(13,148,136,0.08) 0%, transparent 65%)`,
              pointerEvents: 'none',
            }}
          />
        </>
      )}

      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: 420,
          mx: 2,
          animation: reducedMotion ? undefined : `${fadeUp} 0.45s ease-out`,
        }}
      >
        <Box
          sx={{
            borderRadius: '20px',
            overflow: 'hidden',
            background: isDark ? 'rgba(11, 18, 32, 0.82)' : 'rgba(255, 255, 255, 0.94)',
            backdropFilter: 'blur(24px)',
            border: isDark
              ? '1px solid rgba(45, 212, 191, 0.12)'
              : '1px solid rgba(13, 148, 136, 0.15)',
            boxShadow: isDark
              ? '0 32px 64px -16px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.04)'
              : '0 24px 48px -12px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255,255,255,0.9)',
          }}
        >
          {/* Barra de acento clínico */}
          <Box sx={{ height: 3, background: `linear-gradient(90deg, ${TOKENS.teal}, ${TOKENS.primary})` }} />

          <Box sx={{ px: { xs: 3, sm: 4 }, pt: 3.5, pb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box
                component="img"
                src={BRAND_LOGO_LOGIN}
                alt="Klinvia"
                sx={{
                  height: 52,
                  width: 'auto',
                  objectFit: 'contain',
                  filter: isDark
                    ? 'drop-shadow(0 4px 16px rgba(45,212,191,0.25))'
                    : 'drop-shadow(0 2px 8px rgba(13,148,136,0.2))',
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.05rem',
                    letterSpacing: '-0.02em',
                    color: isDark ? 'rgba(255,255,255,0.94)' : '#0F172A',
                    lineHeight: 1.2,
                  }}
                >
                  {STATUS_BY_STEP[stepIndex]}
                </Typography>
                <Typography
                  sx={{
                    mt: 0.35,
                    fontSize: '0.78rem',
                    color: isDark ? TOKENS.slateFaint : TOKENS.slateMuted,
                  }}
                >
                  Preparando ambiente clínico
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 2.5, px: 0.5 }}>
              <EkgTrace color={ekgColor} reducedMotion={reducedMotion} />
            </Box>

            {/* Etapas — assinatura em vez de spinner genérico */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 1,
                pt: 1,
                borderTop: `1px solid ${isDark ? 'rgba(148,163,184,0.12)' : 'rgba(100,116,139,0.12)'}`,
              }}
            >
              {STEPS.map((step, i) => {
                const active = i === stepIndex;
                const done = i < stepIndex;
                return (
                  <Box key={step.id} sx={{ flex: 1, textAlign: 'center' }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        mx: 'auto',
                        mb: 0.75,
                        bgcolor: active || done ? TOKENS.teal : (isDark ? 'rgba(148,163,184,0.25)' : 'rgba(100,116,139,0.25)'),
                        boxShadow: active ? `0 0 0 3px ${isDark ? 'rgba(45,212,191,0.2)' : 'rgba(13,148,136,0.2)'}` : 'none',
                        transition: 'background-color 0.3s, box-shadow 0.3s',
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '0.65rem',
                        fontWeight: active ? 600 : 500,
                        color: active
                          ? (isDark ? TOKENS.tealBright : TOKENS.teal)
                          : (isDark ? TOKENS.slateFaint : TOKENS.slateMuted),
                        letterSpacing: '0.02em',
                        lineHeight: 1.2,
                      }}
                    >
                      {step.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AuthLoadingScreen;
